/* ─────────────────────────────────────────────────────────────
   IMPLIED VOLATILITY TAB
   GET /api/v1/stocks/{ticker}/implied-volatility?window_days=30

   Options-derived expectations for the searched ticker:

     summary cards    ← spot, iv30 (30-day ATM IV), nearest expiry's expected
                        move, the largest move priced on the board, as_of
     expiries table   ← expiries[] — per listed expiry: DTE, ATM strike/IV,
                        the expected (straddle-based) move in % and $, and the
                        spot ∓ move price band. (`implied_move_1sd` is served
                        but not rendered — dropped per Somya, 2026-08-18.)

   Reading the term structure: a jump in ATM IV between two adjacent expiries
   means the market is pricing an event (usually earnings) between those two
   dates — the row where the jump lands is highlighted.

   Notes on units: `iv30`, `atm_iv`, `implied_move_1sd` and `implied_move` are
   ALREADY percentages — never multiplied. `implied_move_dollars`, `band_low`
   and `band_high` are absolute prices.

   Since 2026-10 the endpoint serves a daily series instead of the expiry
   ladder: { tenor_days, source, latest{…}, series[…] } with the same per-row
   fields plus observed_date and spot. Both shapes render.

   Null rule: any field may be null / the list empty (e.g. no listed options).
   Render what's present, hide what's null, never show "N/A".
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getImpliedVolatility } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";
import { fmtDate, fmtPct, fmtNum } from "../../../config/earningsNote";
import {
  NotePage, SectionTitle, TileGrid, Banner,
  Scroller, Table, Th, Td, Tl, NoteSkeleton, NoteEmpty,
  NoteMasthead, NoteFooter,
} from "../Widgets/earningsNoteUI";

/* An IV step-up of this many points vs the prior expiry marks an event row. */
const EVENT_JUMP_PTS = 8;

/* "as_of" arrives as "2026-08-18 03:44:42" — show date + time, null-safe. */
const fmtAsOf = (s) => {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(s))) return fmtDate(s);   /* date-only (series payload) */
  const dt = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(dt.getTime())) return String(s);
  return dt.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const ImpliedVolatility = ({ stock }) => {
  const ticker = stock?.ticker;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      const res = await getImpliedVolatility(ticker);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[90, 120, 280]} />;
  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data)
    return <NoteEmpty>Implied-volatility data is not available for {ticker}.</NoteEmpty>;

  /* Two payload shapes are served:
       ladder  { spot, iv30, window_days, as_of, expiries[] }            (2026-08-18)
       series  { spot, tenor_days, window_days, as_of, source, latest{}, series[] }
               — one ~7-day ATM observation per trading day (2026-10). */
  const { spot, iv30, window_days, as_of, tenor_days } = data;
  const expiries = Array.isArray(data.expiries) ? data.expiries : [];
  const series = Array.isArray(data.series) ? data.series : [];
  const latest = data.latest ?? series[0] ?? null;

  if (spot == null && iv30 == null && expiries.length === 0 && !latest)
    return (
      <NoteEmpty>
        No listed-options data is available for {ticker} — implied volatility needs an options
        chain.
      </NoteEmpty>
    );

  const nearest = expiries[0] ?? latest;

  /* The expiry pricing the biggest expected move — where the event risk sits. */
  const biggest = expiries.reduce(
    (best, e) =>
      e?.implied_move != null && (best == null || Number(e.implied_move) > Number(best.implied_move))
        ? e
        : best,
    null
  );

  /* Flag the row where ATM IV steps up sharply vs the prior expiry. */
  const eventIdx = new Set();
  expiries.forEach((e, i) => {
    if (i === 0) return;
    const prev = expiries[i - 1];
    if (
      e?.atm_iv != null &&
      prev?.atm_iv != null &&
      Number(e.atm_iv) - Number(prev.atm_iv) >= EVENT_JUMP_PTS
    ) {
      eventIdx.add(i);
    }
  });

  /* ── Summary cards ── */
  const tiles = [
    spot != null ? { label: "Spot", value: formatCurrency(spot) } : null,
    iv30 != null
      ? {
          label: `IV${window_days ?? 30}`,
          value: fmtPct(iv30, { signed: false, decimals: 1 }),
          hint: `${window_days ?? 30}-day ATM implied volatility`,
        }
      : null,
    nearest?.implied_move != null
      ? {
          label: `Move by ${fmtDate(nearest.expiry)}`,
          value: `±${fmtPct(nearest.implied_move, { signed: false, decimals: 1 })}`,
          hint:
            nearest.implied_move_dollars != null
              ? `≈ ${formatCurrency(nearest.implied_move_dollars)} either way`
              : null,
        }
      : null,
    biggest?.implied_move != null && biggest !== nearest
      ? {
          label: `Largest priced move (${fmtDate(biggest.expiry)})`,
          value: `±${fmtPct(biggest.implied_move, { signed: false, decimals: 1 })}`,
          hint:
            biggest.band_low != null && biggest.band_high != null
              ? `${formatCurrency(biggest.band_low)} – ${formatCurrency(biggest.band_high)}`
              : null,
          tone: "warn",
        }
      : null,
    expiries.length === 0 && latest?.atm_iv != null
      ? {
          label: `ATM IV (${latest.dte ?? tenor_days ?? 7}d)`,
          value: fmtPct(latest.atm_iv, { signed: false, decimals: 1 }),
          hint: latest.atm_strike != null ? `at the ${formatCurrency(latest.atm_strike)} strike` : null,
        }
      : null,
    expiries.length === 0 && latest?.band_low != null && latest?.band_high != null
      ? {
          label: `Price band to ${fmtDate(latest.expiry)}`,
          value: `${formatCurrency(latest.band_low)} – ${formatCurrency(latest.band_high)}`,
        }
      : null,
    expiries.length > 0
      ? { label: "Listed expiries", value: String(expiries.length) }
      : null,
  ].filter(Boolean);

  return (
    <NotePage>
      {/* ── Masthead ── */}
      <NoteMasthead
        eyebrow="Options-implied expectations"
        title={`${stock?.company_name || ticker} (${data.ticker || ticker}) — Implied Volatility`}
        meta={[
          as_of ? `as of ${fmtAsOf(as_of)}` : null,
          spot != null ? `spot ${formatCurrency(spot)}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      />

      {/* ── Summary cards ── */}
      {tiles.length > 0 && (
        <div style={{ marginTop: "var(--spacing-md)" }}>
          <TileGrid tiles={tiles} min={160} />
        </div>
      )}

      {/* ── Term structure table ── */}
      {expiries.length > 0 && (
        <>
          <SectionTitle caption="per listed expiry · what the options market is pricing in">
            Implied moves by expiry
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Expiry</Th>
                  <Th>DTE</Th>
                  <Th>ATM strike</Th>
                  <Th>ATM IV</Th>
                  <Th>Expected move</Th>
                  <Th>Move ($)</Th>
                  <Th left>Price band</Th>
                </tr>
              </thead>
              <tbody>
                {expiries.map((e, i) => {
                  const isEvent = eventIdx.has(i);
                  const rowTint = isEvent
                    ? { backgroundColor: "rgba(217,119,6,0.08)" }
                    : undefined;
                  return (
                    <tr key={e.expiry ?? i}>
                      <Tl style={rowTint}>
                        {fmtDate(e.expiry)}
                        {isEvent && (
                          <MuiTooltip
                            title="ATM IV steps up sharply vs the prior expiry — the options market is pricing an event (usually earnings) landing between these two dates."
                            placement="top"
                            arrow
                          >
                            <span style={{ color: "var(--color-warning)", cursor: "help" }}> ⚠</span>
                          </MuiTooltip>
                        )}
                      </Tl>
                      <Td tone="muted" style={rowTint}>
                        {e.dte != null ? fmtNum(e.dte, 0) : "—"}
                      </Td>
                      <Td style={rowTint}>
                        {e.atm_strike != null ? formatCurrency(e.atm_strike) : "—"}
                      </Td>
                      <Td bold tone={isEvent ? "warn" : undefined} style={rowTint}>
                        {e.atm_iv != null
                          ? fmtPct(e.atm_iv, { signed: false, decimals: 1 })
                          : "—"}
                      </Td>
                      <Td bold style={rowTint}>
                        {e.implied_move != null
                          ? `±${fmtPct(e.implied_move, { signed: false, decimals: 1 })}`
                          : "—"}
                      </Td>
                      <Td style={rowTint}>
                        {e.implied_move_dollars != null
                          ? `±${formatCurrency(e.implied_move_dollars)}`
                          : "—"}
                      </Td>
                      <Td left tone="muted" style={rowTint}>
                        {e.band_low != null && e.band_high != null
                          ? `${formatCurrency(e.band_low)} – ${formatCurrency(e.band_high)}`
                          : "—"}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
        </>
      )}

      {/* ── Daily history of the short-dated implied move (series payload) ── */}
      {expiries.length === 0 && series.length > 0 && (
        <>
          <SectionTitle caption={`one at-the-money reading per trading day · ~${tenor_days ?? 7}-day expiry`}>
            Implied move — last {window_days ?? 30} days
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Date</Th>
                  <Th>Spot</Th>
                  <Th left>Expiry</Th>
                  <Th>DTE</Th>
                  <Th>ATM IV</Th>
                  <Th>Expected move</Th>
                  <Th>Move ($)</Th>
                  <Th left>Price band</Th>
                </tr>
              </thead>
              <tbody>
                {series.map((e, i) => (
                  <tr key={e.observed_date ?? i}>
                    <Tl>{fmtDate(e.observed_date)}</Tl>
                    <Td>{e.spot != null ? formatCurrency(e.spot) : "—"}</Td>
                    <Tl>{fmtDate(e.expiry)}</Tl>
                    <Td tone="muted">{e.dte != null ? fmtNum(e.dte, 0) : "—"}</Td>
                    <Td bold>{e.atm_iv != null ? fmtPct(e.atm_iv, { signed: false, decimals: 1 }) : "—"}</Td>
                    <Td bold>
                      {e.implied_move != null
                        ? `±${fmtPct(e.implied_move, { signed: false, decimals: 1 })}`
                        : "—"}
                    </Td>
                    <Td>{e.implied_move_dollars != null ? `±${formatCurrency(e.implied_move_dollars)}` : "—"}</Td>
                    <Td left tone="muted">
                      {e.band_low != null && e.band_high != null
                        ? `${formatCurrency(e.band_low)} – ${formatCurrency(e.band_high)}`
                        : "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>
          <Banner kind="plain" style={{ marginTop: "var(--spacing-md)" }}>
            <strong>How to read this: </strong>
            each row is that day's at-the-money straddle on the expiry about a week out — the move
            the options market priced in either direction by that expiry. Rising ATM IV into a
            date usually means an event (earnings) is approaching.
          </Banner>
        </>
      )}

      {/* ── How to read this ── */}
      {expiries.length > 0 && (
        <Banner kind="plain" style={{ marginTop: "var(--spacing-md)" }}>
          <strong>How to read this: </strong>
          the expected move is what the at-the-money straddle prices in either direction by that
          expiry, and the price band is spot ∓ the dollar move. A sharp IV step between two
          adjacent expiries means the market is pricing an event landing between those dates.
        </Banner>
      )}

      <NoteFooter>
        Implied volatilities and moves are derived from listed option prices at the at-the-money
        strike and reflect market expectations, not a forecast. For internal analysis only — not
        investment advice.
      </NoteFooter>
    </NotePage>
  );
};

export default ImpliedVolatility;
